import React from 'react'
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, Text } from '@components'
import { useLanguage } from '@context'

interface NotificationsLoadErrorProps {
  onRetry: () => void
  retrying: boolean
}

/**
 * Shown instead of the empty state when the list could not be loaded and
 * there is nothing to show: « Tout est à jour » would be untrue.
 */
export const NotificationsLoadError = ({
  onRetry,
  retrying,
}: NotificationsLoadErrorProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { t } = useLanguage()

  return (
    <View style={styles.container} testID="notifications-load-error">
      <View style={styles.iconCircle}>
        <Icon name="bell" size={theme.iconSize.jumbo} color={theme.colors.greyBlue} />
      </View>
      <Text type="subtitleBold" color={theme.colors.black} center style={styles.title}>
        {t('notifications.loadError.title')}
      </Text>
      <Text type="bodySmall" color={theme.colors.greyBlue} center>
        {t('notifications.loadError.subtitle')}
      </Text>
      <View style={styles.retry}>
        {retrying ? (
          <ActivityIndicator color={theme.colors.primary} />
        ) : (
          <TouchableOpacity testID="notifications-retry" accessibilityRole="button" onPress={onRetry}>
            <Text type="bodySmallBold" color={theme.colors.primary}>
              {t('notifications.loadError.retry')}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: theme.spacing.xxxl * 2,
      paddingHorizontal: theme.spacing.xl,
    },
    iconCircle: {
      width: 88,
      height: 88,
      borderRadius: 44,
      backgroundColor: theme.colors.greyBlue + '12',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing.lg,
    },
    title: {
      marginBottom: theme.spacing.xs,
    },
    // Fixed height: the spinner replacing the link does not move the block.
    retry: {
      marginTop: theme.spacing.lg,
      minHeight: 24,
      justifyContent: 'center',
    },
  })
